<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKanbanStore } from 'dashboard/stores/kanban';
import { messageStamp } from 'shared/helpers/timeHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { tempoCurto } from './helpers/tempo';

// CRM-04 R2-T7 (K2, §6.3): a linha do tempo única do negócio, da mais nova para a mais velha. As
// mensagens das conversas que a pessoa vê, marcadas com o contato e a caixa; as mudanças de etapa com
// quem moveu; e o resumo do encaminhamento, a única nota privada que o servidor manda, marcado como
// tal. "Carregar mais" pede a página seguinte pelo cursor do servidor.
const props = defineProps({
  cardId: { type: [Number, String], required: true },
  // As etapas do funil do negócio: o evento pode trazer só o id da etapa.
  stages: { type: Array, default: () => [] },
  // As conversas visíveis, para dizer a caixa da conversa que entrou no negócio.
  conversations: { type: Array, default: () => [] },
});

const { t } = useI18n();
const kanban = useKanbanStore();
const timeline = computed(() => kanban.timeline);

// A falha fica na store (`timeline.error`), e a seção diz que não carregou, com "tentar de novo".
const carregar = async () => {
  try {
    await kanban.fetchTimeline(props.cardId);
  } catch {
    // dito na tela
  }
};

watch(
  () => props.cardId,
  id => {
    if (id) carregar();
  },
  { immediate: true }
);

const carregarMais = async () => {
  try {
    await kanban.loadMoreTimeline();
  } catch {
    // dito na tela
  }
};

// Os instantes do Kanban vêm em segundos (`to_i`); um texto de data também serve.
const emSegundos = valor =>
  typeof valor === 'number'
    ? valor
    : Math.floor(new Date(valor).getTime() / 1000);

const quando = item => messageStamp(emSegundos(item.created_at), 'dd/MM HH:mm');

const nomeDaEtapa = etapa => {
  if (etapa === null || etapa === undefined) return null;
  if (typeof etapa === 'object') return etapa.name;
  if (typeof etapa === 'number')
    return props.stages.find(stage => stage.id === etapa)?.name || '—';
  return etapa;
};

const recebida = mensagem =>
  mensagem.message_type === 'incoming' || mensagem.message_type === 0;

// A recebida leva o nome do contato; a enviada, para quem foi (e quem mandou, quando vem).
const autorDaMensagem = mensagem => {
  const nome = mensagem.contact?.name || '';
  if (recebida(mensagem)) return nome;
  return mensagem.sender_name
    ? t('KANBAN.DRAWER.TIMELINE.FROM_TO', {
        from: mensagem.sender_name,
        name: nome,
      })
    : t('KANBAN.DRAWER.TIMELINE.TO', { name: nome });
};

// Quem fez: pessoa, agente, entrada automática, lote ou sistema (o `actor_type` do contrato).
const quemFez = ({ actor_type: tipo, actor_name: nome }) => {
  if (tipo === 'pessoa')
    return nome
      ? t('KANBAN.DRAWER.TIMELINE.ACTOR.pessoa', { name: nome })
      : null;
  if (tipo === 'agente')
    return nome
      ? t('KANBAN.DRAWER.TIMELINE.ACTOR.agente', { name: nome })
      : null;
  if (tipo === 'lote')
    return nome
      ? t('KANBAN.DRAWER.TIMELINE.ACTOR.lote', { name: nome })
      : t('KANBAN.DRAWER.TIMELINE.ACTOR.lote_sem_nome');
  if (tipo === 'entrada') return t('KANBAN.DRAWER.TIMELINE.ACTOR.entrada');
  if (tipo === 'sistema') return t('KANBAN.DRAWER.TIMELINE.ACTOR.sistema');
  return nome || null;
};

const caixaDaConversa = conversationId =>
  props.conversations.find(conversa => conversa.id === conversationId)?.inbox
    ?.name;

const oQueAconteceu = evento => {
  if (evento.kind === 'etapa') {
    const de = nomeDaEtapa(evento.from_stage);
    const para = nomeDaEtapa(evento.to_stage);
    return de
      ? t('KANBAN.DRAWER.TRANSITION', { from: de, to: para })
      : t('KANBAN.DRAWER.TIMELINE.STAGE_ENTERED', { to: para });
  }
  if (evento.kind === 'juntar') return t('KANBAN.DRAWER.TIMELINE.MERGED');
  if (evento.kind === 'contato+')
    return evento.contact?.name
      ? t('KANBAN.DRAWER.TIMELINE.CONTACT_ADDED_NAMED', {
          name: evento.contact.name,
        })
      : t('KANBAN.DRAWER.TIMELINE.CONTACT_ADDED');
  if (evento.kind === 'contato-')
    return evento.contact?.name
      ? t('KANBAN.DRAWER.TIMELINE.CONTACT_REMOVED_NAMED', {
          name: evento.contact.name,
        })
      : t('KANBAN.DRAWER.TIMELINE.CONTACT_REMOVED');
  if (evento.kind === 'conversa+') {
    const caixa = caixaDaConversa(evento.conversation_id);
    return caixa
      ? t('KANBAN.DRAWER.TIMELINE.CONVERSATION_ADDED_IN', { inbox: caixa })
      : t('KANBAN.DRAWER.TIMELINE.CONVERSATION_ADDED');
  }
  return t('KANBAN.DRAWER.TIMELINE.OTHER');
};

const textoDoEvento = evento => {
  const texto = oQueAconteceu(evento);
  const ator = quemFez(evento);
  return ator
    ? t('KANBAN.DRAWER.TIMELINE.WITH_ACTOR', { text: texto, actor: ator })
    : texto;
};

const ICONE_DO_EVENTO = {
  etapa: 'i-lucide-arrow-right',
  juntar: 'i-lucide-merge',
  'contato+': 'i-lucide-user-plus',
  'contato-': 'i-lucide-user-minus',
  'conversa+': 'i-lucide-message-circle-plus',
};

const anexos = mensagem => mensagem.attachments || [];
</script>

<template>
  <section class="flex flex-col gap-2">
    <h3 class="text-xs font-medium uppercase text-n-slate-10">
      {{ $t('KANBAN.DRAWER.TIMELINE.SECTION') }}
    </h3>

    <div v-if="timeline.loading" class="flex justify-center py-3">
      <Spinner class="text-n-brand" />
    </div>

    <p
      v-else-if="!timeline.items.length && !timeline.error"
      class="text-xs text-n-slate-10"
    >
      {{ $t('KANBAN.DRAWER.TIMELINE.EMPTY') }}
    </p>

    <ol v-if="timeline.items.length" class="flex flex-col gap-2">
      <li
        v-for="item in timeline.items"
        :key="`${item.type}:${item.id}`"
        :data-item="item.type"
      >
        <div
          v-if="item.type === 'message'"
          class="flex flex-col gap-0.5 p-2 rounded-lg"
          :class="
            item.private
              ? 'bg-n-amber-2 border border-n-amber-6'
              : 'bg-n-alpha-1'
          "
        >
          <span class="flex items-center gap-1 text-xs text-n-slate-10">
            <Icon
              :icon="
                recebida(item)
                  ? 'i-lucide-arrow-down-left'
                  : 'i-lucide-arrow-up-right'
              "
              class="flex-shrink-0 size-3"
            />
            <span class="truncate">
              {{ autorDaMensagem(item) }}
              <template v-if="item.inbox?.name">
                {{
                  $t('KANBAN.DRAWER.TIMELINE.IN_INBOX', {
                    inbox: item.inbox.name,
                  })
                }}
              </template>
            </span>
            <span class="flex-1" />
            <time class="flex-shrink-0 tabular-nums">{{ quando(item) }}</time>
          </span>
          <span
            v-if="item.private"
            data-resumo-do-encaminhamento
            class="self-start text-xs font-medium text-n-amber-11"
          >
            {{ $t('KANBAN.DRAWER.TIMELINE.HANDOFF_SUMMARY') }}
          </span>
          <span
            v-if="item.content"
            class="text-sm whitespace-pre-line text-n-slate-12"
          >
            {{ item.content }}
          </span>
          <a
            v-for="anexo in anexos(item)"
            :key="anexo.id"
            :href="anexo.data_url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
          >
            <Icon icon="i-lucide-paperclip" class="flex-shrink-0 size-3" />
            {{ anexo.file_type || $t('KANBAN.DRAWER.TIMELINE.ATTACHMENT') }}
          </a>
          <!-- Um disparo que não saiu aparece aqui ou em lugar nenhum: o envio é assíncrono. -->
          <span
            v-if="item.status === 'failed'"
            class="flex items-center gap-1 text-xs text-n-ruby-11"
          >
            <Icon icon="i-lucide-triangle-alert" class="size-3" />
            {{ item.external_error || $t('KANBAN.DRAWER.SEND_FAILED') }}
          </span>
        </div>

        <div v-else class="flex items-start gap-2 text-xs text-n-slate-11">
          <Icon
            :icon="ICONE_DO_EVENTO[item.kind] || 'i-lucide-dot'"
            class="flex-shrink-0 mt-0.5 size-3"
          />
          <span class="flex-1" data-evento>{{ textoDoEvento(item) }}</span>
          <span
            v-if="item.seconds_in_previous_stage"
            class="flex-shrink-0 tabular-nums"
          >
            {{ tempoCurto(item.seconds_in_previous_stage, t) }}
          </span>
          <time class="flex-shrink-0 tabular-nums text-n-slate-10">
            {{ quando(item) }}
          </time>
        </div>
      </li>
    </ol>

    <p
      v-if="timeline.error"
      class="flex items-center gap-2 text-xs text-n-ruby-11"
    >
      {{ $t('KANBAN.DRAWER.TIMELINE.FAILED') }}
      <button
        type="button"
        class="font-medium text-n-brand hover:underline"
        @click="timeline.items.length ? carregarMais() : carregar()"
      >
        {{ $t('KANBAN.DRAWER.TIMELINE.RETRY') }}
      </button>
    </p>

    <button
      v-if="timeline.nextCursor && !timeline.error"
      type="button"
      data-carregar-mais
      :disabled="timeline.loadingMore"
      class="h-8 text-xs font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 disabled:opacity-50"
      @click="carregarMais"
    >
      {{
        timeline.loadingMore
          ? $t('KANBAN.DRAWER.TIMELINE.LOADING')
          : $t('KANBAN.DRAWER.TIMELINE.LOAD_MORE')
      }}
    </button>
  </section>
</template>
